/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { SlDropdown, } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { SHARED_STYLES } from '../css/shared-css.js';
let ChromedashTypeahead = class ChromedashTypeahead extends LitElement {
    constructor() {
        super(...arguments);
        this.slDropdownRef = createRef();
        this.slInputRef = createRef();
        this.value = '';
        this.candidates = [];
        this.prefix = null;
        this.chunkStart = 0;
        this.chunkEnd = 0;
        this.wasDismissed = false;
        this.termWasCompleted = false;
        this.placeholder = '';
    }
    static get styles() {
        return [
            ...SHARED_STYLES,
            css `
        chromedash-typeahead-dropdown {
          width: 100%;
        }
        #inputfield::part(base) {
          background: #eee;
          border: none;
          border-radius: 8px;
        }
      `,
        ];
    }
    _fireEvent(eventName, detail) {
        const event = new CustomEvent(eventName, {
            bubbles: true,
            composed: true,
            detail,
        });
        this.dispatchEvent(event);
    }
    reflectValue(event) {
        if (event) {
            event.stopPropagation();
        }
        const slInput = this.slInputRef.value;
        this.value = slInput.value;
    }
    hide() {
        this.slDropdownRef.value.hide();
    }
    show() {
        this.slDropdownRef.value.show();
    }
    focus() {
        const slInput = this.slInputRef.value;
        slInput?.focus();
    }
    blur() {
        const slInput = this.slInputRef.value;
        slInput?.blur();
    }
    findPrefix() {
        const inputEl = this.slInputRef.value?.input;
        if (!inputEl)
            return;
        const wholeStr = inputEl.value;
        const caret = inputEl.selectionStart || 0;
        if (caret != inputEl.selectionEnd) {
            // User has a range selected.
            this.prefix = null;
            return;
        }
        this.chunkStart = wholeStr.lastIndexOf(' ', caret - 1) + 1;
        this.chunkEnd = wholeStr.indexOf(' ', caret);
        if (this.chunkEnd === -1)
            this.chunkEnd = wholeStr.length;
        this.prefix = wholeStr.substring(this.chunkStart, caret);
    }
    shouldShowCandidate(candidate, prefix) {
        if (prefix === null)
            return false;
        prefix = prefix.toLowerCase();
        const lowerName = candidate.name.toLowerCase();
        const lowerDoc = candidate.doc.toLowerCase();
        return (lowerName.split(/\s+/).some(w => w.startsWith(prefix)) ||
            lowerDoc.split(/\s+/).some(w => w.startsWith(prefix)) ||
            lowerName.split(/\W+/).some(w => w.startsWith(prefix)) ||
            lowerDoc.split(/\W+/).some(w => w.startsWith(prefix)));
    }
    // Return true if the user is still entering the keyword and is not
    // ready to enter an enum value yet.
    shouldGroup(s) {
        if (s === null) {
            return true;
        }
        const COMPARE_OPS = ['=', ':', '<', '>'];
        return !COMPARE_OPS.some(op => s.includes(op));
    }
    groupCandidates(candidates) {
        const groupsSeen = new Set();
        const groupsSeenTwice = new Set();
        for (const c of candidates) {
            if (groupsSeen.has(c.group)) {
                groupsSeenTwice.add(c.group);
            }
            else {
                groupsSeen.add(c.group);
            }
        }
        const groupsSeenTwiceProcessed = new Set();
        const result = [];
        for (const c of candidates) {
            if (!groupsSeenTwice.has(c.group)) {
                result.push(c);
            }
            else if (!groupsSeenTwiceProcessed.has(c.group)) {
                result.push({ group: c.group, name: c.group + ':', doc: c.doc });
                groupsSeenTwiceProcessed.add(c.group);
            }
        }
        return result;
    }
    async handleCandidateSelected(e) {
        const candidateValue = e.detail.item.value;
        const inputEl = this.slInputRef.value?.renderRoot.querySelector('input');
        if (!inputEl)
            return;
        const wholeStr = inputEl.value;
        // Don't add a space after the completed value: let the user type it.
        const newWholeStr = wholeStr.substring(0, this.chunkStart) +
            candidateValue +
            wholeStr.substring(this.chunkEnd, wholeStr.length);
        this.slInputRef.value.value = newWholeStr;
        this.reflectValue();
        // Wait for the sl-input to propagate its new value to its <input> before
        // setting or accessing the text selection.
        await this.updateComplete;
        this.chunkStart = this.chunkStart + candidateValue.length;
        this.chunkEnd = this.chunkStart;
        inputEl.selectionStart = this.chunkStart;
        inputEl.selectionEnd = this.chunkEnd;
        // A term was completed iff there is no other term that the user could
        // further complete by typing or selecting.
        const possibleExtensions = this.vocabulary.filter(c => c.name.startsWith(candidateValue));
        this.termWasCompleted = possibleExtensions.length <= 1;
        this.calcCandidates();
        // The user may have clicked a menu item, causing the sl-input to lose
        // keyboard focus.  So, focus on the sl-input again.
        inputEl.focus();
    }
    // Check if the user is pressing Enter to send a query.  This is detected
    // on keyDown so that the handler is run before the dropdown keyDown is run.
    // If stopPropagation() is not called, the event will cause a completion.
    handleInputFieldKeyDown(event) {
        if (event.key === 'Enter') {
            const slDropdown = this.slDropdownRef.value;
            if (!slDropdown)
                return;
            const currentItem = slDropdown.getCurrentItem();
            if (!slDropdown.open || !currentItem) {
                // User wants to send the query to the server.
                this._fireEvent('sl-change', this);
                event.stopPropagation();
            }
        }
    }
    // As the user types and moves the caret, keep recalculating a-c choices.
    // Left and right movement is handled on keyUp so that caret has already been
    // moved to its new position before this handler is run.
    handleInputFieldKeyUp(event) {
        if (['Escape'].includes(event.key)) {
            this.wasDismissed = true;
            return;
        }
        if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
            this.wasDismissed = false;
            return;
        }
        this.termWasCompleted = false;
        this.calcCandidates();
    }
    calcCandidates(event) {
        if (event) {
            event.stopPropagation();
        }
        this.findPrefix();
        this.candidates = this.vocabulary.filter(c => this.shouldShowCandidate(c, this.prefix));
        if (this.shouldGroup(this.prefix)) {
            this.candidates = this.groupCandidates(this.candidates);
        }
        const slDropdown = this.slDropdownRef.value;
        if (!slDropdown)
            return;
        if (this.candidates.length > 0 &&
            !this.wasDismissed &&
            !this.termWasCompleted) {
            slDropdown.show();
        }
        else {
            slDropdown.hide();
        }
    }
    renderInputField() {
        return html `
      <sl-input
        id="inputfield"
        slot="trigger"
        placeholder=${this.placeholder}
        value=${live(this.value)}
        ${ref(this.slInputRef)}
        autocomplete="off"
        spellcheck="false"
        @keydown="${this.handleInputFieldKeyDown}"
        @keyup="${this.handleInputFieldKeyUp}"
        @focus="${this.calcCandidates}"
        @click="${this.calcCandidates}"
        @sl-change="${this.reflectValue}"
        @sl-input="${this.reflectValue}"
      >
        <slot name="prefix" slot="prefix"></slot>
        <slot name="suffix" slot="suffix"></slot>
      </sl-input>
    `;
    }
    renderAutocompleteMenu() {
        // sl-menu automatically selects the first menu item.  This nerfs
        // that undesired behavior by adding an invisible 0th menu item.
        const nonselectableItem = html `
      <chromedash-typeahead-invisible-item></chromedash-typeahead-invisible-item>
    `;
        return html `
      <sl-menu
        @click=${e => e.preventDefault()}
        @sl-select=${this.handleCandidateSelected}
      >
        ${nonselectableItem}
        ${this.candidates.map(c => html `
            <chromedash-typeahead-item
              value=${c.name}
              doc=${c.doc}
              prefix=${this.prefix ?? nothing}
            ></chromedash-typeahead-item>
          `)}
      </sl-menu>
    `;
    }
    render() {
        return html `
      <chromedash-typeahead-dropdown
        stay-open-on-select
        sync="width"
        ${ref(this.slDropdownRef)}
      >
        ${this.renderInputField()} ${this.renderAutocompleteMenu()}
      </chromedash-typeahead-dropdown>
    `;
    }
};
__decorate([
    property()
], ChromedashTypeahead.prototype, "value", void 0);
__decorate([
    state()
], ChromedashTypeahead.prototype, "candidates", void 0);
__decorate([
    state()
], ChromedashTypeahead.prototype, "prefix", void 0);
__decorate([
    state()
], ChromedashTypeahead.prototype, "chunkStart", void 0);
__decorate([
    state()
], ChromedashTypeahead.prototype, "chunkEnd", void 0);
__decorate([
    state()
], ChromedashTypeahead.prototype, "wasDismissed", void 0);
__decorate([
    state()
], ChromedashTypeahead.prototype, "termWasCompleted", void 0);
__decorate([
    property({ type: String })
], ChromedashTypeahead.prototype, "placeholder", void 0);
__decorate([
    property({ type: Array, attribute: false })
], ChromedashTypeahead.prototype, "vocabulary", void 0);
ChromedashTypeahead = __decorate([
    customElement('chromedash-typeahead')
], ChromedashTypeahead);
export { ChromedashTypeahead };
let ChromedashTypeaheadDropdown = class ChromedashTypeaheadDropdown extends SlDropdown {
    getCurrentItem() {
        const item = this.getMenu().getCurrentItem();
        if (!item || item instanceof ChromedashTypeaheadInvisibleItem) {
            return undefined;
        }
        return item;
    }
    setCurrentItem(newCurrentItem) {
        const menu = this.getMenu();
        menu.setCurrentItem(newCurrentItem);
        newCurrentItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    getAllVisibleItems(menu) {
        const items = menu.getAllItems();
        return items.filter(item => !(item instanceof ChromedashTypeaheadInvisibleItem));
    }
    resetSelection() {
        const currentItem = this.getCurrentItem();
        currentItem?.setAttribute('tabindex', '-1');
    }
    async handleTriggerKeyDown(event) {
        const menu = this.getMenu();
        if (!menu) {
            return;
        }
        const menuItems = this.getAllVisibleItems(menu);
        if (menuItems.length === 0) {
            return;
        }
        const currentItem = menu.getCurrentItem();
        // Handle menu selection keys.
        if (['Enter'].includes(event.key)) {
            event.preventDefault();
            if (this.open && currentItem) {
                currentItem.click();
                this.resetSelection();
            }
        }
        // Handle menu navigation keys.
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            event.stopPropagation();
            // Show the menu if it's not already open
            if (!this.open) {
                this.show();
            }
            if (currentItem) {
                const currentItemIndex = menuItems.indexOf(currentItem);
                if (event.key === 'ArrowDown' && menuItems[currentItemIndex + 1]) {
                    this.setCurrentItem(menuItems[currentItemIndex + 1]);
                }
                if (event.key === 'ArrowUp' && menuItems[currentItemIndex - 1]) {
                    this.setCurrentItem(menuItems[currentItemIndex - 1]);
                }
            }
            else {
                if (event.key === 'ArrowDown') {
                    this.setCurrentItem(menuItems[1]);
                }
                if (event.key === 'ArrowUp') {
                    this.setCurrentItem(menuItems[menuItems.length - 1]);
                }
            }
            // Note: We keep keyboard focus on #inputfield.
        }
    }
};
ChromedashTypeaheadDropdown = __decorate([
    customElement('chromedash-typeahead-dropdown')
], ChromedashTypeaheadDropdown);
export { ChromedashTypeaheadDropdown };
let ChromedashTypeaheadItem = class ChromedashTypeaheadItem extends LitElement {
    constructor() {
        super(...arguments);
        this.value = '';
        this.doc = '';
        this.prefix = '';
        // tabindex is initially -1 for menu items so that the tab key does not
        // navigate among menu items.  Instead, sl-menu handles arrow keys and
        // mouseover.  Shoelace uses tabindex==0 only for the current menu item.
        // https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex
        this.tabindex = -1;
        this.role = 'menuitem';
    }
    static get styles() {
        return [
            ...SHARED_STYLES,
            css `
        .menu-item {
          display: flex;
          flex-wrap: wrap;
          font-family: var(--sl-font-sans);
          font-size: var(--sl-font-size-medium);
          font-weight: var(--sl-font-weight-normal);
          line-height: var(--sl-line-height-normal);
          letter-spacing: var(--sl-letter-spacing-normal);
          color: var(--sl-color-neutral-700);
          padding: var(--sl-spacing-2x-small) var(--sl-spacing-2x-small);
          transition: var(--sl-transition-fast) fill;
          user-select: none;
          -webkit-user-select: none;
          white-space: nowrap;
          cursor: pointer;
        }

        .active {
          outline: none;
          background-color: var(--sl-color-primary-200);
          opacity: 1;
        }
        #value {
          width: 24em;
          overflow-x: hidden;
        }
        code {
          font-size: 85%;
          background: #eee;
          padding: var(--content-padding-quarter);
        }
      `,
        ];
    }
    handleMouseOver(event) {
        if (this.parentElement) {
            this.parentElement.setCurrentItem(this);
        }
        event.stopPropagation();
    }
    highlight(s) {
        const start = s.toLowerCase().indexOf(this.prefix.toLowerCase());
        if (start === -1)
            return s;
        const before = s.substring(0, start);
        const matching = s.substring(start, start + this.prefix.length);
        const after = s.substring(start + this.prefix.length);
        return html `${before}<b>${matching}</b>${after}`;
    }
    render() {
        const highlightedValue = this.highlight(this.value);
        const highlightedDoc = this.highlight(this.doc);
        return html `
      <div
        class="menu-item ${this.tabindex === 0 ? 'active' : ''}"
        @mouseover=${this.handleMouseOver}
      >
        <span id="value"><code>${highlightedValue}</code></span>
        <span id="doc">${highlightedDoc}</span>
      </div>
    `;
    }
};
__decorate([
    property({ type: String })
], ChromedashTypeaheadItem.prototype, "value", void 0);
__decorate([
    property({ type: String })
], ChromedashTypeaheadItem.prototype, "doc", void 0);
__decorate([
    property({ type: String })
], ChromedashTypeaheadItem.prototype, "prefix", void 0);
__decorate([
    property({ type: Number })
], ChromedashTypeaheadItem.prototype, "tabindex", void 0);
__decorate([
    property({ type: String, reflect: true })
], ChromedashTypeaheadItem.prototype, "role", void 0);
ChromedashTypeaheadItem = __decorate([
    customElement('chromedash-typeahead-item')
], ChromedashTypeaheadItem);
export { ChromedashTypeaheadItem };
let ChromedashTypeaheadInvisibleItem = class ChromedashTypeaheadInvisibleItem extends ChromedashTypeaheadItem {
    render() {
        return html `${nothing}`;
    }
};
ChromedashTypeaheadInvisibleItem = __decorate([
    customElement('chromedash-typeahead-invisible-item')
], ChromedashTypeaheadInvisibleItem);
export { ChromedashTypeaheadInvisibleItem };
